import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Lock, X } from "lucide-react";
import JeaButton from "@/components/jea/JeaButton";

/**
 * SignUpModal — shown when a demo visitor tries a write action.
 * Same structure as PaywallModal but tuned to acquisition copy.
 */
const SignUpModal = ({ open, onClose, feature = "this feature" }) => {
  const nav = useNavigate();
  const goSignUp = () => { onClose?.(); nav("/register"); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            data-testid="signup-modal"
          >
            <div className="w-full max-w-[440px] bg-bg-secondary border border-border-default rounded-[16px] p-8 shadow-elevated"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-full bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center">
                  <Lock size={18} className="text-accent-pink" />
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 text-text-secondary hover:text-white"
                  data-testid="signup-modal-close"
                >
                  <X size={16} />
                </button>
              </div>
              <h2 className="text-white font-bold text-[18px]" data-testid="signup-modal-title">
                Sign up to continue
              </h2>
              <p className="text-text-secondary text-[13px] mt-2 leading-6">
                This feature requires a real account. Sign up to run real broker queries against your
                own debtors — <span className="text-white">{feature}</span> and more.
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors"
                  data-testid="signup-modal-continue"
                >
                  Continue browsing demo
                </button>
                <JeaButton onClick={goSignUp} className="gap-2" data-testid="signup-modal-signup">
                  <Sparkles size={14} /> Sign Up Free
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SignUpModal;
